import * as flatbuffers from "flatbuffers";
import { tracePointEmit } from "../../integrationTrace";
import { TracePointId } from "../generated/integration-trace/trace-point-id";

// ROOT
import { UiCmd } from "../generated/ui-cmd/ui-cmd";

// CONTROL MESSAGE
import { ControlMessage } from "../generated/control/control-message";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessage } from "../generated/control/elevator-control-message";
import { ControlVariant } from "../generated/control/control-variant";
import { NormalControl } from "../generated/control/normal-control";
import { Control } from "../generated/control/control";

// UUID TABLE + U128 STRUCT
import { Uuid } from "../generated/front-end/uuid";
import { U128 } from "../generated/u128/u128";

// ======================================================================================
//  UUID → U128 EXACT MATCH WITH BACKEND (CRITICAL FIX)
// ======================================================================================
function uuidToBackendU128(uuid: string): { upper: bigint; lower: bigint } {
  const hex = uuid.replace(/-/g, "");
  if (hex.length !== 32) throw new Error(`Invalid UUID format: ${uuid}`);

  // The backend's U128 is the UUID's 16 bytes read big-endian: `upper` holds
  // time_low, time_mid and time_hi (the first 16 hex digits), `lower` holds
  // clock_seq and node (the last 16). Each field already sits at its bit
  // offset within its half, so parsing the half whole is the packed value.
  return {
    upper: BigInt(`0x${hex.slice(0, 16)}`),
    lower: BigInt(`0x${hex.slice(16, 32)}`),
  };
}

// ======================================================================================
//  Create Uuid table with U128 struct
// ======================================================================================
function createUuid(builder: flatbuffers.Builder, uuid: string): number {
  const { upper, lower } = uuidToBackendU128(uuid);

  // Create U128 struct
  const u128Offset = U128.createU128(builder, upper, lower);

  // Wrap inside Uuid table
  return Uuid.createUuid(builder, u128Offset);
}

// ======================================================================================
//  Build Elevator Control Message
// ======================================================================================
function createControlMessageForElevator(
  builder: flatbuffers.Builder,
  params: { message: Control; position: number },
) {
  const normalCtrlOffset = NormalControl.createNormalControl(builder, params.message);
  const elevCtrlOffset = ElevatorControlMessage.createElevatorControlMessage(
    builder,
    ControlVariant.NormalControl,
    normalCtrlOffset,
    params.position,
  );
  return ControlMessage.createControlMessage(
    builder,
    DeviceControlMessage.ElevatorControlMessage,
    elevCtrlOffset,
  );
}

// ======================================================================================
//  UI CMD Wrapper
// ======================================================================================
function createTimestamp(): bigint {
  return BigInt(Date.now());
}

function buildUiCmd(
  builder: flatbuffers.Builder,
  params: {
    msgOffset: number;
    active: boolean;
    channelId: string;
    bankId: string;
    liftId?: string;
  },
) {
  const channelIdOffset = createUuid(builder, params.channelId);
  const bankIdOffset = createUuid(builder, params.bankId);
  const liftIdOffset = params.liftId ? createUuid(builder, params.liftId) : undefined;

  const timestamp = createTimestamp();

  UiCmd.startUiCmd(builder);

  UiCmd.addMsg(builder, params.msgOffset);
  UiCmd.addActive(builder, params.active);
  UiCmd.addChannelId(builder, channelIdOffset);
  UiCmd.addBankId(builder, bankIdOffset);
  UiCmd.addTimestamp(builder, timestamp);

  if (liftIdOffset !== undefined) {
    UiCmd.addLiftId(builder, liftIdOffset);
  }

  return UiCmd.endUiCmd(builder);
}

// ======================================================================================
//  PUBLIC FUNCTION: Build Final UiCmd Payload
// ======================================================================================
/**
 * Serialises one elevator control command as a finished `UiCmd` FlatBuffer.
 *
 * The control message, channel, bank and (optional) lift ids and a timestamp
 * go into a single root table; the returned bytes are what the monitoring
 * socket sends for a command.
 *
 * @throws Error "Invalid UUID format" when channelId, bankId or a given liftId is not
 *   32 characters once dashes are removed (an empty id included), and SyntaxError
 *   when those characters are not hex digits.
 */
export default function createUiCmdPayload(params: {
  message: Control;
  position: number;

  active: boolean;
  channelId: string;
  bankId: string;
  liftId?: string;
}) {
  const builder = new flatbuffers.Builder(1024);

  // Build elevator control message
  const msgOffset = createControlMessageForElevator(builder, {
    message: params.message,
    position: params.position,
  });

  // Build UI CMD root object
  const uiCmdOffset = buildUiCmd(builder, {
    msgOffset,
    active: params.active,
    channelId: params.channelId,
    bankId: params.bankId,
    liftId: params.liftId,
  });

  // IMPORTANT: Finish buffer correctly
  builder.finish(uiCmdOffset);

  // TP 414: FrontendUiCmdBuilt — UiCmd FlatBuffer payload successfully built
  tracePointEmit(TracePointId.FrontendUiCmdBuilt);

  return builder.asUint8Array();
}
// ======================================================================================
// flatc --ts -I src\flatbuffers\schemas -I src\flatbuffers\schemas\util -o src\flatbuffers\generated src\flatbuffers\schemas\ui_message.fbs
// flatc --ts --gen-object-api -I src/flatbuffers/schemas -o src/flatbuffers/generated src/flatbuffers/schemas/**/*.fbs
// ======================================================================================
